import React from 'react';

import { CircularProgress } from '@mui/material';
import { useForkRef } from '@mui/material/utils';

import { StyledAutocomplete } from 'components/Input/styles';

import { canScroll, isNearBottom } from 'utils/scroll';

import { LoadingRow } from './styles';
import { InfiniteAutocompleteProps, InfiniteListState } from './types';

const InfiniteListContext = React.createContext<InfiniteListState>({
  hasMore: false,
  loadingMore: false,
  onLoadMore: () => undefined,
});

// Must stay at module scope: a listbox recreated per render remounts and resets the scroll position.
const InfiniteListbox = React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLUListElement>>(
  ({ children, onScroll, ...props }, ref) => {
    const { hasMore, loadingMore, onLoadMore } = React.useContext(InfiniteListContext);
    const listRef = React.useRef<HTMLUListElement | null>(null);
    const handleRef = useForkRef(ref, listRef);

    React.useEffect(() => {
      if (hasMore && !loadingMore && listRef.current && !canScroll(listRef.current)) {
        onLoadMore();
      }
    });

    return (
      <ul
        {...props}
        ref={handleRef}
        onScroll={(event) => {
          onScroll?.(event);
          if (hasMore && isNearBottom(event.currentTarget)) {
            onLoadMore();
          }
        }}
      >
        {children}
        {loadingMore && (
          <LoadingRow data-testid="infinite-autocomplete-loading">
            <CircularProgress size={20} />
          </LoadingRow>
        )}
      </ul>
    );
  }
);

const InfiniteAutocompleteInner = <
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>(
  {
    hasMore,
    loadingMore,
    onLoadMore,
    slotProps,
    ...props
  }: InfiniteAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  ref: React.ForwardedRef<HTMLDivElement>
): React.ReactElement => (
  <InfiniteListContext.Provider value={{ hasMore, loadingMore, onLoadMore }}>
    <StyledAutocomplete
      filterOptions={(options) => options}
      {...props}
      ref={ref}
      slotProps={{ ...slotProps, listbox: { ...slotProps?.listbox, component: InfiniteListbox } }}
    />
  </InfiniteListContext.Provider>
);

const InfiniteAutocomplete = React.forwardRef(InfiniteAutocompleteInner) as <
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>(
  props: InfiniteAutocompleteProps<T, Multiple, DisableClearable, FreeSolo> & { ref?: React.Ref<HTMLDivElement> }
) => React.ReactElement;

export default InfiniteAutocomplete;
